/**
 * Combobox — the vendored searchable-picker composition (WEB TIER A · WT2).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * THE ONE COMPONENT THE APP GENUINELY LACKED — the shadcn sandbox verdict's
 * headline win ("a real combobox"). Composition: `@radix-ui/react-popover`
 * (portalled panel matched to its trigger's width) + `cmdk` (filtering,
 * highlight movement, listbox/option semantics). Both are individual
 * packages added at WT2 — never the `radix-ui` umbrella, no shadcn CLI in the
 * repo, no runtime shadcn import; this file is CRF-owned source the CLI can
 * never revert.
 *
 * THE LIST SPEAKS forms.html's `.lb` GRAMMAR (the owner-approved mockup):
 *  · rows are `role="option"` inside a `role="listbox"` labelled by
 *    `listLabel` ("Products in India");
 *  · the highlighted row is the `on` row — `accent/indigo-soft` fill + a 3px
 *    `accent/indigo` left rail + `aria-selected`, three encodings, never
 *    colour alone;
 *  · EVERY GLYPH IN A ROW IS `text-fg` — the mockup's own measurement: the
 *    highlight fill would put `text/secondary` at 4.42, so the quiet parts of
 *    a row differ by SIZE (body13/mono), not colour;
 *  · the right-aligned detail slot (the price) renders THE EM DASH for
 *    valueless rows at full contrast — the product's own NO_VALUE, not a
 *    blank;
 *  · the footer action row offers to create from what was typed (the party
 *    picker's "that is a route, not a form inside a form").
 *
 * NO_VALUE IS RE-DECLARED HERE, NOT IMPORTED, deliberately: the canonical
 * constant lives in `features/inquiries/format.ts:54`, and `components/ui`
 * importing from `features/` is the inverted layering `SelectField`'s move to
 * this directory exists to forbid. Same glyph, provenance recorded;
 * `combobox.test.tsx` pins the equality by value.
 *
 * SANDBOX-MEASURED FIXES FOLDED AT VENDOR TIME (pinned by
 * `vendored-source.test.ts`):
 *  · dark styling only via the app's `:root[data-theme='dark']` `:where()`
 *    variant — tokens re-map themselves, no `.dark` selector may appear here;
 *  · THE TRIGGER RIM IS `border-rim` (≡ text/secondary, 4.64/5.85) — the WT1
 *    control-rim rule: `border-line`/`border-strong` measure ~1.4:1 on a
 *    surface and are hairlines, not control edges;
 *  · DISABLED IS BORDERED-INERT: dashed `border-line-strong`, full-contrast
 *    text, `aria-disabled` — never `disabled:opacity` (measured 2.11:1);
 *  · highlight and hover from authored tokens (`bg-indigo-soft`, `bg-subtle`)
 *    — no `/NN` alpha fades;
 *  · radii: `rounded-sm`/`rounded-md`/`rounded-pill` — authored tokens only;
 *  · quiet text is `text-fg-secondary`, never `text-fg-muted`.
 *
 * WT6 — THE ASYNC ENABLER (ledger item 14, additive): a SERVER-searched caller
 * (the party pickers: `GET /parties?q=`, two-character floor) owns the
 * query→options loop itself, so it needs two things this component did not
 * have: `onQueryChange` to surface what was typed, and `serverFiltered` to
 * BYPASS cmdk's client-side filter entirely — the options handed in are
 * already the server's answer to the query, and filtering them again would
 * second-guess the wire (and hide results whose match was on a column the
 * client cannot see). `emptyText` stays the caller's sentence, which is what
 * lets a server picker speak its whole state ladder — "type two characters",
 * "Searching…", the route's refusal, "no match" — through one honest slot.
 * Rows gain an optional `sub` second line (source · city on a party row);
 * every glyph in it stays `text-fg` — the mockup's own measurement: on the
 * highlight fill the quiet TOKEN would fail, so the quiet LINE differs by
 * size, not colour.
 */

import { useId, useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { Command } from 'cmdk';
import { Check, ChevronDown, Plus } from 'lucide-react';
import { cn } from '../../lib/cn';

/** `features/inquiries/format.ts`'s em dash — see the header for why it is re-declared. */
const NO_VALUE = '—';

export interface ComboboxOption {
  /** The committed identity — what `onSelect` receives. */
  value: string;
  /** The primary glyph run: the name. */
  label: string;
  /** Optional mono run beside the label (the product code). */
  code?: string;
  /**
   * The right-aligned detail slot — a PREFORMATTED string (money formatting
   * belongs to the feature's own `format`/`money` machinery, not to a ui
   * primitive). `null` renders NO_VALUE at the slot's full size; `undefined`
   * renders no slot at all (pickers whose rows carry no detail column).
   */
  detail?: string | null;
  /** Optional warn-toned note on the row ("not in the seeded catalogue"). */
  note?: string;
  /**
   * Optional quiet second line under the label (a party row's
   * "Prospect · Chennai"). Rendered at `text-label` size in full-contrast
   * `text-fg` — inside the highlight fill the quiet colour token measures
   * ~4.4, so the hierarchy is carried by SIZE (the header's measured rule).
   */
  sub?: string;
}

export interface ComboboxProps {
  options: readonly ComboboxOption[];
  /** The chosen option's value, or null when nothing is chosen yet. */
  value: string | null;
  onSelect: (value: string) => void;
  /** Accessible name for the listbox AND the trigger: "Products in India". */
  listLabel: string;
  /** Trigger text before anything is chosen. */
  placeholder: string;
  searchPlaceholder?: string;
  /** The honest no-matches sentence. */
  emptyText?: string;
  /**
   * The footer action row — create from what was typed. Receives the current
   * query (may be empty). Rendering is the caller's sentence via
   * `createLabel`; omitting both omits the row.
   */
  onCreate?: (query: string) => void;
  /** The footer row's sentence, given what was typed. */
  createLabel?: (query: string) => string;
  /**
   * WT6 async enabler (header): surfaces every change of the search query so a
   * server-searched caller can drive its request from it. The query is also
   * reported as '' when the panel closes — the caller's search should not keep
   * running for a panel that is gone.
   */
  onQueryChange?: (query: string) => void;
  /**
   * TRUE when the options handed in are ALREADY the server's answer to the
   * current query: cmdk's client-side filtering is switched off entirely, so
   * the rows render exactly as the wire ordered them and none is hidden by a
   * second, client-side opinion of what matches.
   */
  serverFiltered?: boolean;
  disabled?: boolean;
  id?: string;
}

export function Combobox({
  options,
  value,
  onSelect,
  listLabel,
  placeholder,
  searchPlaceholder = 'Type to filter…',
  emptyText = 'No matches.',
  onCreate,
  createLabel,
  onQueryChange,
  serverFiltered = false,
  disabled = false,
  id,
}: ComboboxProps): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const reactId = useId();
  const triggerId = id ?? `combobox-${reactId}`;
  const chosen = options.find((option) => option.value === value);
  const patchQuery = (next: string): void => {
    setQuery(next);
    onQueryChange?.(next);
  };
  // A fresh open starts unfiltered: the query lives only as long as the panel.
  const closeAndReset = (next: boolean): void => {
    setOpen(next);
    if (!next) patchQuery('');
  };

  return (
    <Popover.Root open={open} onOpenChange={closeAndReset}>
      <Popover.Trigger
        id={triggerId}
        role="combobox"
        disabled={disabled}
        aria-disabled={disabled || undefined}
        aria-label={listLabel}
        className={cn(
          // `bg-surface`, NOT the FIELD_BOX `bg-subtle`: text/secondary on
          // bg/subtle is 4.40 — the pair this repo has already refused twice —
          // while on bg/surface both the placeholder and the rim hold their
          // measured 4.64/5.85. The rim carries the control's edge.
          'flex h-9 w-full items-center justify-between gap-2 rounded-sm border bg-surface px-3 text-left text-body',
          // Interactive rim vs bordered-inert (header): the two states differ
          // by rim and cursor, NEVER by dimming.
          disabled
            ? 'cursor-not-allowed border-dashed border-line-strong'
            : 'border-rim',
          'text-fg',
        )}
      >
        {/* The placeholder is quiet by tokened colour — text/secondary, 4.64
          * on the surface; the chosen value is always full text-fg. */}
        <span className={cn('truncate', chosen === undefined && 'text-fg-secondary')}>
          {chosen === undefined ? placeholder : chosen.label}
        </span>
        <ChevronDown aria-hidden="true" className="size-3.5 shrink-0 text-fg-secondary" />
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={4}
          // Radix publishes the trigger's measured width on this variable —
          // the panel matches its control exactly, the sandbox's "portalled
          // popover matching its trigger" win.
          className="z-50 w-[var(--radix-popover-trigger-width)] min-w-[260px] overflow-hidden rounded-md border border-line bg-surface shadow-[var(--crf-shadow-overlay)]"
        >
          <Command
            // A SERVER-searched caller's options are already the answer to the
            // query — no second, client-side opinion (header, WT6 enabler).
            shouldFilter={!serverFiltered}
            // cmdk's default scoring reorders rows by match quality; the
            // server already ordered the options (country, shelf, sort_order,
            // code — SelectField's recorded rule), so filtering must FILTER,
            // never re-rank. A simple contains-match keeps the wire order.
            filter={(_itemValue, search, keywords) => {
              const haystack = (keywords ?? []).join(' ').toLowerCase();
              return haystack.includes(search.trim().toLowerCase()) ? 1 : 0;
            }}
          >
            <Command.Input
              value={query}
              onValueChange={patchQuery}
              placeholder={searchPlaceholder}
              className="h-9 w-full border-b border-line bg-transparent px-3 text-body text-fg outline-none placeholder:text-fg-secondary"
            />
            <Command.List
              /*
               * ⚠ WT6 CORRECTION, measured — `label`, NOT `aria-label`.
               * cmdk's List spreads the caller's props FIRST and then writes
               * `aria-label` from its OWN `label` prop
               * (`dist/index.mjs`: `{...c, "cmdk-list":"", role:"listbox",
               * "aria-label": u}` where `u = props.label`). An `aria-label`
               * handed in was therefore overwritten with `undefined` and the
               * listbox shipped with NO accessible name — while this file's
               * header claimed the rows sit "inside a `role="listbox"`
               * labelled by `listLabel`". WT2's pins used an unnamed
               * `findAllByRole('listbox')`, so nothing caught it; the WT6
               * builder suite needed to tell the item listbox apart from the
               * page's other listboxes and that is what surfaced it. The name
               * is now pinned BY NAME in `combobox.test.tsx`.
               */
              label={listLabel}
              className="max-h-[300px] overflow-y-auto p-1"
            >
              <Command.Empty className="px-3 py-6 text-center text-body13 text-fg-secondary">
                {emptyText}
              </Command.Empty>
              {options.map((option) => (
                <Command.Item
                  key={option.value}
                  value={option.value}
                  keywords={[option.label, option.code ?? '']}
                  onSelect={() => {
                    onSelect(option.value);
                    closeAndReset(false);
                  }}
                  className={cn(
                    // The `.lb-o` row: 36px min-height, hairline geometry from
                    // padding, and the `on` treatment = indigo-soft fill + 3px
                    // indigo rail + aria-selected (three encodings).
                    'relative flex min-h-9 cursor-pointer select-none items-center justify-between gap-3 rounded-sm py-1.5 pl-2.5 pr-2.5',
                    'border-l-[3px] border-l-transparent text-fg outline-none',
                    'data-[selected=true]:border-l-indigo data-[selected=true]:bg-indigo-soft',
                  )}
                >
                  <span className="flex min-w-0 flex-col gap-0.5">
                    <span className="flex min-w-0 items-center gap-2">
                      <span className="truncate text-body13">{option.label}</span>
                      {option.code === undefined ? null : (
                        <span className="shrink-0 text-mono">{option.code}</span>
                      )}
                      {option.note === undefined ? null : (
                        <span className="shrink-0 rounded-pill bg-warn-bg px-1.5 text-label text-warn-fg">
                          {option.note}
                        </span>
                      )}
                      {option.value === value ? (
                        <Check aria-hidden="true" className="size-3.5 shrink-0 text-indigo" />
                      ) : null}
                    </span>
                    {option.sub === undefined ? null : (
                      // Full-contrast text-fg at label size — see the header's
                      // measured rule for why the quiet LINE is not the quiet
                      // COLOUR inside a highlightable row.
                      <span className="truncate text-label">{option.sub}</span>
                    )}
                  </span>
                  {option.detail === undefined ? null : (
                    <span className="shrink-0 text-body13 font-medium tnum">
                      {option.detail ?? NO_VALUE}
                    </span>
                  )}
                </Command.Item>
              ))}
              {onCreate === undefined ? null : (
                <Command.Item
                  // The footer row is reachable whatever was typed — creating
                  // from a query that matched nothing is its whole purpose.
                  forceMount
                  value="__create__"
                  keywords={[]}
                  onSelect={() => {
                    onCreate(query);
                    closeAndReset(false);
                  }}
                  className={cn(
                    'relative mt-1 flex min-h-9 cursor-pointer select-none items-center gap-2 rounded-sm border-t border-line py-1.5 pl-2.5 pr-2.5',
                    'border-l-[3px] border-l-transparent text-body13 text-fg outline-none',
                    'data-[selected=true]:border-l-indigo data-[selected=true]:bg-indigo-soft',
                  )}
                >
                  <Plus aria-hidden="true" className="size-3.5 shrink-0 text-indigo" />
                  {createLabel === undefined
                    ? query.trim() === ''
                      ? 'Create new…'
                      : `Create "${query.trim()}"…`
                    : createLabel(query)}
                </Command.Item>
              )}
            </Command.List>
          </Command>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
